import React, { useState } from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth.store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CreditCard,
  Building,
  Briefcase,
  Shield,
  FileText,
  Clock,
  Pencil,
  Save,
  X,
  HeartHandshake,
  Heart,
  Home,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { InfoCell } from "@/components/shared/InfoCell";
import { DigitalEmployeeBadge } from "@/components/shared/DigitalEmployeeBadge";

import { toVnDateString } from "@apv-erp/status";
export default function UserProfilePage() {
  const queryClient = useQueryClient();
  const { user: authUser, setUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});

  const userId = authUser?.id || "";

  // Query user details from DB
  const { data: userProfile, isLoading } = useQuery({
    queryKey: ["user-profile", userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const user = await trpcClient.users.getById.query({ id: userId });
        return user;
      } catch (err) {
        console.warn("Could not fetch user by ID, using auth user fallback", err);
        return authUser;
      }
    },
    enabled: !!userId,
  });

  const activeUser: any = userProfile || authUser || {};

  const startEdit = () => {
    setFormData({
      fullName: activeUser.fullName || "",
      phone: activeUser.phone || "",
      gender: activeUser.gender || "Nam",
      dateOfBirth: activeUser.dateOfBirth
        ? toVnDateString(activeUser.dateOfBirth)
        : "",
      placeOfBirth: activeUser.placeOfBirth || "",
      maritalStatus: activeUser.maritalStatus || "Độc thân",
      idCardNumber: activeUser.idCardNumber || "",
      idCardIssueDate: activeUser.idCardIssueDate
        ? toVnDateString(activeUser.idCardIssueDate)
        : "",
      idCardIssuePlace: activeUser.idCardIssuePlace || "Cục CSQLHC về TTXH",
      address: activeUser.address || "",
      currentAddress: activeUser.currentAddress || "",
      emergencyContact: activeUser.emergencyContact || "",
      department: activeUser.department || "Ban Giám Đốc",
      position: activeUser.position || "Quản trị viên",
      taxNumber: activeUser.taxNumber || "",
      socialInsuranceNumber: activeUser.socialInsuranceNumber || "",
      bankName: activeUser.bankName || "Vietcombank",
      bankAccountNumber: activeUser.bankAccountNumber || "1028394857",
      bankBranch: activeUser.bankBranch || "Chi nhánh Long An",
      avatar: activeUser.avatar || null,
      signature: activeUser.signature || null,
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormData({});
  };

  const handleFieldChange = (key: string, val: string) => {
    setFormData((prev) => ({ ...prev, [key]: val }));
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (dataToSave: Record<string, any>) => {
      if (!userId) throw new Error("Không xác định được ID người dùng");
      return await trpcClient.users.update.mutate({
        id: userId,
        data: dataToSave,
      });
    },
    onSuccess: async (updated) => {
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
      if (authUser && updated) {
        setUser({
          ...authUser,
          fullName: updated.fullName || authUser.fullName,
          email: updated.email || authUser.email,
        });
      }
      toast.success("Đã cập nhật hồ sơ cá nhân thành công!");
    },
    onError: (err: any) => {
      toast.error(`Lỗi cập nhật hồ sơ: ${err.message || "Vui lòng thử lại"}`);
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleAvatarChange = async (url: string) => {
    if (isEditing) {
      setFormData((prev) => ({ ...prev, avatar: url }));
    } else if (userId) {
      try {
        await trpcClient.users.update.mutate({
          id: userId,
          data: { avatar: url },
        });
        await queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
        toast.success("Cập nhật ảnh đại diện thành công!");
      } catch (err: any) {
        toast.error(`Lỗi lưu avatar: ${err.message}`);
      }
    }
  };

  const handleSignatureChange = async (url: string) => {
    if (isEditing) {
      setFormData((prev) => ({ ...prev, signature: url }));
    } else if (userId) {
      try {
        await trpcClient.users.update.mutate({
          id: userId,
          data: { signature: url },
        });
        await queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });
        toast.success("Cập nhật mẫu chữ ký thành công!");
      } catch (err: any) {
        toast.error(`Lỗi lưu chữ ký: ${err.message}`);
      }
    }
  };

  const formatDate = (val?: string | Date | null) => {
    if (!val) return "Chưa cập nhật";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return String(val);
    }
  };

  const currentVal = (key: string, fallback = "") => {
    if (isEditing) return formData[key] ?? fallback;
    return activeUser[key] ?? fallback;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <Clock className="w-8 h-8 mx-auto mb-2 text-indigo-500 animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Đang nạp hồ sơ nhân sự...</p>
        </div>
      </div>
    );
  }

    const actions = (
    <div className="flex items-center gap-2">
      {isEditing ? (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={cancelEdit}
            className="h-9 text-xs font-semibold"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Hủy
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="h-9 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
          >
            <Save className="w-3.5 h-3.5 mr-1" />{" "}
            {updateMutation.isPending ? "Đang lưu..." : "Lưu thay đổi"}
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          onClick={startEdit}
          className="h-9 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
        >
          <Pencil className="w-3.5 h-3.5 mr-1" /> Chỉnh sửa hồ sơ
        </Button>
      )}
    </div>
  );

  return (
    <StandardDetailLayout
      title="Hồ Sơ Cá Nhân"
      status={
        <Badge variant="outline" className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border-indigo-200">
          ● Cán bộ nhân viên APV
        </Badge>
      }
      breadcrumbs={[
        { label: "Hồ sơ", to: "/profile" },
        { label: activeUser.fullName || "Thông tin cá nhân" },
      ]}
      actions={actions}
    >
      <div className="w-full mx-auto py-2 space-y-6 max-w-7xl">{/* ── 2-Zone Layout: Left (Badge & Payroll) vs Right (4-Col HRM Grid) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ── Left Column (~33% / 4 cols): Digital Employee Badge & Security ── */}
        <div className="lg:col-span-4 space-y-4">
          <DigitalEmployeeBadge
            user={{
              id: activeUser.id,
              fullName: currentVal("fullName", "Admin User"),
              employeeCode: activeUser.employeeCode || activeUser.userId || "EMP-001",
              position: currentVal("position", "Quản trị viên"),
              department: currentVal("department", "Ban Giám Đốc"),
              email: activeUser.email || "admin@apv-manager.com",
              phone: currentVal("phone", "0987 654 321"),
              avatar: isEditing ? formData.avatar : activeUser.avatar,
              signature: isEditing ? formData.signature : activeUser.signature,
              bankName: currentVal("bankName", "Vietcombank"),
              bankAccountNumber: currentVal("bankAccountNumber", "1028394857"),
              bankBranch: currentVal("bankBranch", "Chi nhánh Long An"),
              status: activeUser.status || "ACTIVE",
              joinDate: activeUser.joinDate,
            }}
            isEditing={isEditing}
            onAvatarChange={handleAvatarChange}
            onSignatureChange={handleSignatureChange}
          />

          {/* System Security Card */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-2 text-xs text-slate-600">
            <div className="flex items-center gap-1.5 font-bold text-slate-800 text-[11px] uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-indigo-600" /> Quyền Hạn & Bảo Mật Hệ Thống
            </div>
            <div className="space-y-1 text-[11.5px]">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Vai trò:</span>
                <Badge className="bg-indigo-600 text-white hover:bg-indigo-600 text-[10px] py-0 px-2">
                  {activeUser.role || "ADMIN"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Trạng thái tài khoản:</span>
                <span className="font-semibold text-emerald-700">Hoạt động bình thường</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Ngày gia nhập:</span>
                <span className="font-mono text-slate-700">{formatDate(activeUser.joinDate || "2023-01-01")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column (~67% / 8 cols): 4-Column Merge Cell HRM Grid ── */}
        <div className="lg:col-span-8 space-y-5">
          {/* Card: Hồ Sơ Cán Bộ & Thông Tin Công Tác */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="py-3 px-5 bg-slate-50/70 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Hồ Sơ Nhân Viên & Dữ Liệu Nhân Sự
              </CardTitle>
              <Badge variant="outline" className="text-[10px] text-slate-500 font-mono">
                Lưới 4 Cột Mergecell
              </Badge>
            </CardHeader>

            <CardContent className="p-5 space-y-6">
              {/* ── KHỐI 1: NHÂN THÂN & ĐỊNH DANH CÁ NHÂN ── */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <User className="w-3.5 h-3.5 text-indigo-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    1. Nhân Thân & Định Danh Pháp Lý
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {/* Họ và tên: col-span-2 */}
                  <InfoCell
                    label="Họ và tên"
                    value={currentVal("fullName", "Admin User")}
                    colSpan={2}
                    icon={<User className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    editValue={formData.fullName || ""}
                    onChange={(val) => handleFieldChange("fullName", val)}
                    valueClassName="text-sm font-bold text-indigo-950 uppercase"
                  />

                  {/* Mã nhân viên: col-span-1 */}
                  <InfoCell
                    label="Mã nhân viên"
                    value={activeUser.employeeCode || activeUser.userId || "EMP-001"}
                    colSpan={1}
                    icon={<Briefcase className="w-3.5 h-3.5" />}
                    copyable={true}
                    copyTooltip="Sao chép Mã NV"
                    isEditing={false}
                    valueClassName="font-mono font-bold text-slate-800"
                  />

                  {/* Giới tính: col-span-1 */}
                  <InfoCell
                    label="Giới tính"
                    value={currentVal("gender", "Nam")}
                    colSpan={1}
                    icon={<User className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    editValue={formData.gender || ""}
                    onChange={(val) => handleFieldChange("gender", val)}
                  />

                  {/* Ngày sinh: col-span-1 */}
                  <InfoCell
                    label="Ngày sinh"
                    value={formatDate(currentVal("dateOfBirth", "1990-01-01"))}
                    colSpan={1}
                    icon={<Calendar className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    type="text"
                    editValue={formData.dateOfBirth || ""}
                    placeholder="YYYY-MM-DD"
                    onChange={(val) => handleFieldChange("dateOfBirth", val)}
                  />

                  {/* Nơi sinh: col-span-1 */}
                  <InfoCell
                    label="Nơi sinh / Quê quán"
                    value={currentVal("placeOfBirth", "Hà Nội")}
                    colSpan={1}
                    icon={<Home className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    editValue={formData.placeOfBirth || ""}
                    onChange={(val) => handleFieldChange("placeOfBirth", val)}
                  />

                  {/* Tình trạng hôn nhân: col-span-2 */}
                  <InfoCell
                    label="Tình trạng hôn nhân"
                    value={currentVal("maritalStatus", "Đã kết hôn")}
                    colSpan={2}
                    icon={<Heart className="w-3.5 h-3.5 text-rose-500" />}
                    isEditing={isEditing}
                    editValue={formData.maritalStatus || ""}
                    onChange={(val) => handleFieldChange("maritalStatus", val)}
                  />

                  {/* Số CCCD: col-span-2 */}
                  <InfoCell
                    label="Số CCCD / Hộ chiếu"
                    value={currentVal("idCardNumber", "001090012345")}
                    colSpan={2}
                    icon={<Shield className="w-3.5 h-3.5 text-indigo-500" />}
                    copyable={true}
                    copyTooltip="Sao chép CCCD"
                    isEditing={isEditing}
                    editValue={formData.idCardNumber || ""}
                    onChange={(val) => handleFieldChange("idCardNumber", val)}
                    valueClassName="font-mono font-bold text-slate-800"
                  />

                  {/* Ngày cấp: col-span-1 */}
                  <InfoCell
                    label="Ngày cấp CCCD"
                    value={formatDate(currentVal("idCardIssueDate", "2021-05-15"))}
                    colSpan={1}
                    icon={<Calendar className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    editValue={formData.idCardIssueDate || ""}
                    placeholder="YYYY-MM-DD"
                    onChange={(val) => handleFieldChange("idCardIssueDate", val)}
                  />

                  {/* Nơi cấp: col-span-1 */}
                  <InfoCell
                    label="Nơi cấp"
                    value={currentVal("idCardIssuePlace", "Cục CSQLHC về TTXH")}
                    colSpan={1}
                    icon={<Building className="w-3.5 h-3.5" />}
                    isEditing={isEditing}
                    editValue={formData.idCardIssuePlace || ""}
                    onChange={(val) => handleFieldChange("idCardIssuePlace", val)}
                  />
                </div>
              </div>

              {/* ── KHỐI 2: LIÊN LẠC & NƠI CƯ TRÚ ── */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <Phone className="w-3.5 h-3.5 text-emerald-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    2. Kênh Liên Lạc & Địa Chỉ Cư Trú
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {/* Số điện thoại: col-span-2 */}
                  <InfoCell
                    label="Số điện thoại di động"
                    value={currentVal("phone", "0987 654 321")}
                    colSpan={2}
                    icon={<Phone className="w-3.5 h-3.5 text-emerald-500" />}
                    copyable={true}
                    href={currentVal("phone") ? `tel:${currentVal("phone")}` : undefined}
                    isEditing={isEditing}
                    editValue={formData.phone || ""}
                    onChange={(val) => handleFieldChange("phone", val)}
                    valueClassName="font-mono font-bold text-emerald-700"
                  />

                  {/* Email: col-span-2 */}
                  <InfoCell
                    label="Email công việc"
                    value={activeUser.email || "admin@apv-manager.com"}
                    colSpan={2}
                    icon={<Mail className="w-3.5 h-3.5 text-sky-500" />}
                    copyable={true}
                    href={activeUser.email ? `mailto:${activeUser.email}` : undefined}
                    isEditing={false}
                    valueClassName="font-mono text-xs font-semibold text-sky-700"
                  />

                  {/* Địa chỉ thường trú: col-span-4 */}
                  <InfoCell
                    label="Địa chỉ thường trú (Theo CCCD)"
                    value={currentVal("address", "Số 12, Ngõ 45, Phố Duy Tân, Phường Dịch Vọng Hậu, Quận Cầu Giấy, Hà Nội")}
                    colSpan={4}
                    icon={<MapPin className="w-3.5 h-3.5 text-rose-500" />}
                    isEditing={isEditing}
                    type="textarea"
                    editValue={formData.address || ""}
                    onChange={(val) => handleFieldChange("address", val)}
                    valueClassName="text-xs sm:text-sm font-medium text-slate-800"
                  />

                  {/* Nơi ở hiện tại: col-span-4 */}
                  <InfoCell
                    label="Nơi ở hiện tại (Tạm trú / Liên lạc)"
                    value={currentVal("currentAddress", "Chung cư Roman Plaza, Tố Hữu, Đại Mỗ, Nam Từ Liêm, Hà Nội")}
                    colSpan={4}
                    icon={<Home className="w-3.5 h-3.5 text-indigo-500" />}
                    isEditing={isEditing}
                    type="textarea"
                    editValue={formData.currentAddress || ""}
                    onChange={(val) => handleFieldChange("currentAddress", val)}
                    valueClassName="text-xs sm:text-sm font-medium text-slate-800"
                  />

                  {/* Liên hệ khẩn cấp: col-span-4 */}
                  <InfoCell
                    label="Người liên hệ khẩn cấp"
                    value={currentVal("emergencyContact", "Nguyễn Thị B (Vợ) - SĐT: 0912 345 678")}
                    colSpan={4}
                    icon={<AlertCircle className="w-3.5 h-3.5 text-amber-500" />}
                    isEditing={isEditing}
                    editValue={formData.emergencyContact || ""}
                    onChange={(val) => handleFieldChange("emergencyContact", val)}
                  />
                </div>
              </div>

              {/* ── KHỐI 3: VỊ TRÍ CÔNG TÁC, THUẾ & BHXH ── */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <Briefcase className="w-3.5 h-3.5 text-indigo-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    3. Vị Trí Công Tác, Thuế TNCN & BHXH
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {/* Phòng ban: col-span-2 */}
                  <InfoCell
                    label="Phòng ban trực thuộc"
                    value={currentVal("department", "Ban Giám Đốc")}
                    colSpan={2}
                    icon={<Building className="w-3.5 h-3.5 text-indigo-500" />}
                    isEditing={isEditing}
                    editValue={formData.department || ""}
                    onChange={(val) => handleFieldChange("department", val)}
                    valueClassName="font-bold text-slate-800"
                  />

                  {/* Chức danh: col-span-2 */}
                  <InfoCell
                    label="Chức vụ / Vị trí đảm nhiệm"
                    value={currentVal("position", "Quản trị viên")}
                    colSpan={2}
                    icon={<Briefcase className="w-3.5 h-3.5 text-slate-500" />}
                    isEditing={isEditing}
                    editValue={formData.position || ""}
                    onChange={(val) => handleFieldChange("position", val)}
                    valueClassName="font-semibold text-slate-700"
                  />

                  {/* Ngày vào làm: col-span-1 */}
                  <InfoCell
                    label="Ngày vào làm việc"
                    value={formatDate(activeUser.joinDate || "2023-01-01")}
                    colSpan={1}
                    icon={<Calendar className="w-3.5 h-3.5" />}
                    isEditing={false}
                  />

                  {/* Ngày chính thức: col-span-1 */}
                  <InfoCell
                    label="Ngày ký HĐ chính thức"
                    value={formatDate(activeUser.officialDate || "2023-03-01")}
                    colSpan={1}
                    icon={<Calendar className="w-3.5 h-3.5" />}
                    isEditing={false}
                  />

                  {/* MST cá nhân: col-span-1 */}
                  <InfoCell
                    label="Mã số thuế cá nhân"
                    value={currentVal("taxNumber", "8012345678")}
                    colSpan={1}
                    icon={<FileText className="w-3.5 h-3.5" />}
                    copyable={true}
                    copyTooltip="Sao chép MST cá nhân"
                    isEditing={isEditing}
                    editValue={formData.taxNumber || ""}
                    onChange={(val) => handleFieldChange("taxNumber", val)}
                    valueClassName="font-mono font-bold text-indigo-700"
                  />

                  {/* Số sổ BHXH: col-span-1 */}
                  <InfoCell
                    label="Mã số sổ BHXH"
                    value={currentVal("socialInsuranceNumber", "0123456789")}
                    colSpan={1}
                    icon={<HeartHandshake className="w-3.5 h-3.5" />}
                    copyable={true}
                    copyTooltip="Sao chép Mã BHXH"
                    isEditing={isEditing}
                    editValue={formData.socialInsuranceNumber || ""}
                    onChange={(val) => handleFieldChange("socialInsuranceNumber", val)}
                    valueClassName="font-mono font-bold text-slate-800"
                  />
                </div>
              </div>

              {/* ── KHỐI 4: TÀI KHOẢN NGÂN HÀNG NHẬN LƯƠNG ── */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                  <CreditCard className="w-3.5 h-3.5 text-amber-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    4. Tài Khoản Ngân Hàng Nhận Lương (Payroll)
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {/* Số tài khoản: col-span-2 */}
                  <InfoCell
                    label="Số tài khoản ngân hàng"
                    value={currentVal("bankAccountNumber", "1028394857")}
                    colSpan={2}
                    icon={<CreditCard className="w-3.5 h-3.5 text-amber-500" />}
                    copyable={true}
                    copyTooltip="Sao chép STK"
                    isEditing={isEditing}
                    editValue={formData.bankAccountNumber || ""}
                    onChange={(val) => handleFieldChange("bankAccountNumber", val)}
                    valueClassName="font-mono font-bold text-base text-slate-900"
                  />

                  {/* Tên ngân hàng: col-span-1 */}
                  <InfoCell
                    label="Ngân hàng thụ hưởng"
                    value={currentVal("bankName", "Vietcombank")}
                    colSpan={1}
                    icon={<Building className="w-3.5 h-3.5 text-slate-500" />}
                    isEditing={isEditing}
                    editValue={formData.bankName || ""}
                    onChange={(val) => handleFieldChange("bankName", val)}
                    valueClassName="font-semibold text-slate-800"
                  />

                  {/* Chi nhánh: col-span-1 */}
                  <InfoCell
                    label="Chi nhánh mở thẻ"
                    value={currentVal("bankBranch", "Chi nhánh Long An")}
                    colSpan={1}
                    icon={<MapPin className="w-3.5 h-3.5 text-slate-400" />}
                    isEditing={isEditing}
                    editValue={formData.bankBranch || ""}
                    onChange={(val) => handleFieldChange("bankBranch", val)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </StandardDetailLayout>
  );
}
